import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PortalShell } from "../components/PortalShell";
import { BrokerOnly } from "../components/BrokerOnly";
import { brokerApi } from "../api/broker";
import type { BrokerMaterialsResponse } from "../api/types";

// Portal Materials Library — broker audience. Visible-but-locked
// pre-Certified, unlocked after. The materials API returns the full
// list with a `gated` flag (true until Certified), so we render the
// tiles with a "Locked until Certified" banner over them rather than
// hiding everything. Downloads stay gated server-side and happen via a
// short-lived SAS URL fetched from /broker/materials/{id}/download.

export function BrokerMaterialsPage() {
  return (
    <BrokerOnly>
      <MaterialsBody />
    </BrokerOnly>
  );
}

function MaterialsBody() {
  const [response, setResponse] = useState<BrokerMaterialsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const m = await brokerApi.listMaterials();
        if (cancelled) return;
        setResponse(m);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Couldn't load materials.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <PortalShell pageTitle="Materials">
        <p className="text-small text-status-error" role="alert">{error}</p>
      </PortalShell>
    );
  }
  if (!response) {
    return (
      <PortalShell pageTitle="Materials">
        <p className="text-small text-gray-mid">Loading…</p>
      </PortalShell>
    );
  }

  const { materials, gated } = response;

  const handleDownload = async (id: string) => {
    setDownloadError(null);
    setDownloadingId(id);
    try {
      const dl = await brokerApi.downloadMaterial(id);
      // Open in a new tab — the SAS URL is short-lived and the browser
      // handles the file-type-appropriate disposition.
      window.open(dl.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Couldn't start download.");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <PortalShell pageTitle="Materials">
      <h1 className="text-h2 font-display text-onyx m-0">Materials library</h1>
      {gated && (
        <div className="mt-space-4 p-space-4 border border-border-light rounded-md bg-white-warm/40" data-testid="materials-locked">
          <p className="text-small text-onyx m-0">Locked until you're certified.</p>
          <p className="text-caption text-gray-mid m-0 mt-space-1">
            Finish the onboarding course to unlock the broker materials library.{" "}
            <Link to="/portal/broker/course" className="underline">Continue onboarding</Link>
          </p>
        </div>
      )}
      {downloadError && (
        <p className="mt-space-2 text-small text-status-error" role="alert">{downloadError}</p>
      )}
      {materials.length === 0 ? (
        <p className="text-small text-gray-mid mt-space-4">No materials available yet.</p>
      ) : (
        <ul className="mt-space-4 flex flex-col gap-space-3 list-none p-0">
          {materials.map((m) => (
            <li key={m.id} className="p-space-3 border border-border-light rounded-md bg-white-pure flex items-start justify-between gap-space-3" data-testid={`material-${m.id}`}>
              <div className="min-w-0">
                <p className="text-small font-medium text-onyx m-0">{m.title}</p>
                {m.description && <p className="text-caption text-gray-mid m-0 mt-space-1">{m.description}</p>}
                <p className="text-caption text-gray-mid m-0 mt-space-1">
                  {m.category ?? "General"} · {m.originalFileName}
                </p>
              </div>
              <button
                type="button"
                className="text-small underline text-onyx disabled:text-gray-mid disabled:no-underline"
                onClick={() => handleDownload(m.id)}
                disabled={gated || downloadingId === m.id}
                data-testid={`material-download-${m.id}`}
              >
                {gated ? "Locked" : downloadingId === m.id ? "Opening…" : "Download"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </PortalShell>
  );
}
